<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells><h1 class="title">On-Site payments</h1></app-cells>
      <app-input
        @input.native="onPaymentSearch"
        v-model="search"
        class="input--300"
        type="search"
        placeholder="Search by ID, group name, client name"
        name="search"
      />
      <template v-if="table_pending.items.length">
        <app-cells><h2 class="title">Pending cash ({{ new Intl.NumberFormat('ru-RU').format(stats.on_site_cash_total) }} USD)</h2></app-cells>
        <v-client-table
          :data="table_pending.items"
          :columns="table_pending.columns"
          :options="table_pending.options"
          class="table-default"
          :class="{ 'table-default--loading': table_pending_preloader }"
        >
          <template #h__id><div class="table-default__left">ID</div></template>
          <template #id="{ row }">
            <router-link
              :to="{ name: 'finance-onsite-payments-detail', params: { id: row.id } }"
              class="table-default__link-border"
            >
              Payment ID: {{ row.id }}
            </router-link>
          </template>
          <template #posted="{ row }">
            <div class="table-default__controls">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.created_by?.crop?.crop_url)" alt="Avatar"
                  v-tooltip="{ content: `${row.created_by?.full_name || ''}`, placement: 'top-start', offset: 10 }"
                >
              </div>
              {{ dateTimeFormat(row.created_at) }}
            </div>
          </template>
          <template #group="{ row }">{{ row.client_group?.name || '-' }}</template>
          <template #cash="{ row }">
            <span class="status-tag" v-if="row.stats.cash > 0" style="background-color: #77b017">{{ row.stats.cash }}</span>
            <span v-else>{{ row.stats.cash }}</span>
          </template>
          <template #cc="{ row }">
            <span class="status-tag" v-if="row.stats.cc_payments > 0" style="background-color: #77b017">{{ row.stats.cc_payments }}</span>
            <span v-else>{{ row.stats.cc_payments }}</span>
          </template>
          <template #other="{ row }">
            <span class="status-tag" v-if="row.stats.other_payments > 0" style="background-color: #77b017">{{ row.stats.other_payments }}</span>
            <span v-else>{{ row.stats.other_payments }}</span>
          </template>
          <template #gears="{ row }">
            <span class="status-tag" v-if="row.stats.gears > 0" style="background-color: #77b017">{{ row.stats.gears }}</span>
            <span v-else>{{ row.stats.gears }}</span>
          </template>
          <template #additional="{ row }">
            <span class="status-tag" v-if="row.stats.additional_services > 0" style="background-color: #77b017">{{ row.stats.additional_services }}</span>
            <span v-else>{{ row.stats.additional_services }}</span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationPending"
              v-if="pagination_pending.pages > 1"
              v-model="pagination_pending.page"
              :page-count="pagination_pending.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_pending.count" :count="table_pending.items.length" :page="pagination_pending.page" />
        </app-cells>
      </template>
      <app-cells><h2 class="title">Waiting review</h2></app-cells>
      <v-client-table
        :data="table_review.items"
        :columns="table_review.columns"
        :options="table_review.options"
        class="table-default"
        :class="{ 'table-default--loading': table_review_preloader }"
      >
        <template #h__id><div class="table-default__left">ID</div></template>
        <template #id="{ row }">
          <router-link
            :to="{ name: 'finance-onsite-payments-detail', params: { id: row.id } }"
            class="table-default__link-border"
          >
            Payment ID: {{ row.id }}
          </router-link>
        </template>
        <template #posted="{ row }">
          <div class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.created_by?.crop?.crop_url)" alt="Avatar"
                v-tooltip="{ content: `${row.created_by?.full_name || ''}`, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.created_at) }}
          </div>
        </template>
        <template #group="{ row }">{{ row.client_group?.name || '-' }}</template>
        <template #cash="{ row }">
          <span class="status-tag" v-if="row.stats.cash > 0" style="background-color: #77b017">{{ row.stats.cash }}</span>
          <span v-else>{{ row.stats.cash }}</span>
        </template>
        <template #cc="{ row }">
          <span class="status-tag" v-if="row.stats.cc_payments > 0" style="background-color: #77b017">{{ row.stats.cc_payments }}</span>
          <span v-else>{{ row.stats.cc_payments }}</span>
        </template>
        <template #other="{ row }">
          <span class="status-tag" v-if="row.stats.other_payments > 0" style="background-color: #77b017">{{ row.stats.other_payments }}</span>
          <span v-else>{{ row.stats.other_payments }}</span>
        </template>
        <template #gears="{ row }">
          <span class="status-tag" v-if="row.stats.gears > 0" style="background-color: #77b017">{{ row.stats.gears }}</span>
          <span v-else>{{ row.stats.gears }}</span>
        </template>
        <template #additional="{ row }">
          <span class="status-tag" v-if="row.stats.additional_services > 0" style="background-color: #77b017">{{ row.stats.additional_services }}</span>
          <span v-else>{{ row.stats.additional_services }}</span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationReview"
            v-if="pagination_review.pages > 1"
            v-model="pagination_review.page"
            :page-count="pagination_review.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_review.count" :count="table_review.items.length" :page="pagination_review.page" />
      </app-cells>
      <app-cells><h2 class="title">Completed</h2></app-cells>
      <v-client-table
        :data="table_completed.items"
        :columns="table_completed.columns"
        :options="table_completed.options"
        class="table-default"
        :class="{ 'table-default--loading': table_completed_preloader }"
      >
        <template #h__id><div class="table-default__left">ID</div></template>
        <template #id="{ row }">
          <router-link
            :to="{ name: 'finance-onsite-payments-detail', params: { id: row.id } }"
            class="table-default__link-border"
          >
            Payment ID: {{ row.id }}
          </router-link>
        </template>
        <template #posted="{ row }">
          <div class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.created_by?.crop?.crop_url)" alt="Avatar"
                v-tooltip="{ content: `${row.created_by?.full_name || ''}`, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.created_at) }}
          </div>
        </template>
        <template #group="{ row }">{{ row.client_group?.name || '-' }}</template>
        <template #cash="{ row }">
          <span class="status-tag" v-if="row.stats.cash > 0" style="background-color: #77b017">{{ row.stats.cash }}</span>
          <span v-else>{{ row.stats.cash }}</span>
        </template>
        <template #cc="{ row }">
          <span class="status-tag" v-if="row.stats.cc_payments > 0" style="background-color: #77b017">{{ row.stats.cc_payments }}</span>
          <span v-else>{{ row.stats.cc_payments }}</span>
        </template>
        <template #other="{ row }">
          <span class="status-tag" v-if="row.stats.other_payments > 0" style="background-color: #77b017">{{ row.stats.other_payments }}</span>
          <span v-else>{{ row.stats.other_payments }}</span>
        </template>
        <template #gears="{ row }">
          <span class="status-tag" v-if="row.stats.gears > 0" style="background-color: #77b017">{{ row.stats.gears }}</span>
          <span v-else>{{ row.stats.gears }}</span>
        </template>
        <template #additional="{ row }">
          <span class="status-tag" v-if="row.stats.additional_services > 0" style="background-color: #77b017">{{ row.stats.additional_services }}</span>
          <span v-else>{{ row.stats.additional_services }}</span>
        </template>
        <template #completed="{ row }">
          <div class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.payment_control.completed_by.crop.crop_url)" alt="Avatar"
                v-tooltip="{ content: row.payment_control.completed_by.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            {{ dateTimeFormat(row.payment_control.completed_at) }}
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationCompleted"
            v-if="pagination_completed.pages > 1"
            v-model="pagination_completed.page"
            :page-count="pagination_completed.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_completed.count" :count="table_completed.items.length" :page="pagination_completed.page" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getFinancePaymentsOnSiteList, getFinanceAnalyticsStats } from '@/http'
import { format, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'

export default {
  name: 'FinanceOnSiteList',
  data() {
    return {
      page_loaded: false,
      search: '',
      stats: {},
      table_pending: {
        items: [],
        columns: [
          'id',
          'posted',
          'group',
          'cash',
          'cc',
          'other',
          'gears',
          'additional',
        ],
        options: {
          headings: {
            'id': 'ID',
            'posted': 'Posted by',
            'group': 'Group',
            'cash': 'Cash',
            'cc': 'CC',
            'other': 'Other',
            'gears': 'Gears',
            'additional': 'Additional',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_pending_preloader: false,
      pagination_pending: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_review: {
        items: [],
        columns: [
          'id',
          'posted',
          'group',
          'cash',
          'cc',
          'other',
          'gears',
          'additional',
        ],
        options: {
          headings: {
            'id': 'ID',
            'posted': 'Posted by',
            'group': 'Group',
            'cash': 'Cash',
            'cc': 'CC',
            'other': 'Other',
            'gears': 'Gears',
            'additional': 'Additional',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_review_preloader: false,
      pagination_review: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_completed: {
        items: [],
        columns: [
          'id',
          'posted',
          'group',
          'cash',
          'cc',
          'other',
          'gears',
          'additional',
          'completed',
        ],
        options: {
          headings: {
            'id': 'ID',
            'posted': 'Posted by',
            'group': 'Group',
            'cash': 'Cash',
            'cc': 'CC',
            'other': 'Other',
            'gears': 'Gears',
            'additional': 'Additional',
            'completed': 'Completed by',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_completed: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_completed_preloader: false,
    }
  },
  created() {
    Promise.all([getFinanceAnalyticsStats(), this.fetchItemsPending(), this.fetchItemsCompleted(), this.fetchItemsReview()])
      .then(response => {
        this.stats = response[0].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItemsPending() {
      this.table_pending_preloader = true
      const response = await getFinancePaymentsOnSiteList({
        page: this.pagination_pending.page,
        page_size: 5,
        pending_cash: true,
        search: this.search,
      })
      this.table_pending.items = response.data.results
      this.pagination_pending.pages = response.data.pages
      this.pagination_pending.count = response.data.count
      this.table_pending_preloader = false
    },
    async fetchItemsReview() {
      this.table_review_preloader = true
      const response = await getFinancePaymentsOnSiteList({
        page: this.pagination_review.page,
        page_size: 20,
        completed: false,
        search: this.search,
      })
      this.table_review.items = response.data.results
      this.pagination_review.pages = response.data.pages
      this.pagination_review.count = response.data.count
      this.table_review_preloader = false
    },
    async fetchItemsCompleted() {
      this.table_completed_preloader = true
      const response = await getFinancePaymentsOnSiteList({
        page: this.pagination_completed.page,
        page_size: 20,
        completed: true,
        ordering: '-payment_control__completed_at',
        search: this.search,
      })
      this.table_completed.items = response.data.results
      this.pagination_completed.pages = response.data.pages
      this.pagination_completed.count = response.data.count
      this.table_completed_preloader = false
    },
    onClickPaginationPending(page) {
      this.pagination_pending.page = page
      this.fetchItemsPending()
    },
    onClickPaginationReview(page) {
      this.pagination_review.page = page
      this.fetchItemsReview()
    },
    onClickPaginationCompleted(page) {
      this.pagination_completed.page = page
      this.fetchItemsCompleted()
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    onPaymentSearch() {
      this.searchPayment(this)
    },
    searchPayment: debounce(vm => {
      vm.pagination_pending.page = 1
      vm.pagination_review.page = 1
      vm.pagination_completed.page = 1
      vm.fetchItemsPending()
      vm.fetchItemsCompleted()
      vm.fetchItemsReview()
    }, 350),
  }
}
</script>
